import FilterListIcon from '@mui/icons-material/FilterList';
import {
  Box,
  Button,
  Chip,
  Divider,
  Grid,
  IconButton,
  MenuItem,
  Paper,
  TextField,
  Typography,
} from '@mui/material';
import {
  AgentView,
  CallableFunctionName,
  ExportToCsv,
  Firebase,
  GroupView,
  PublishedResidualOptionsResults,
  SearchableAgentView,
  SearchCriteria,
  useApiContainer,
  useCallable,
} from '@ozark/common';
import {
  ActiveFilter,
  AutoCompleteInputBase,
  Table as DataTable,
  FilterOption,
  Loading,
  Square,
  Title,
} from '@ozark/common/components';
import {PaginatedResponse} from '@ozark/functions/src/functions/express/private/types/PaginatedResponse';
import {ReferralOverrideResidual} from '@ozark/functions/src/functions/express/private/types/Residual';
import {CancelTokenSource} from 'axios';
import {isEmpty} from 'lodash';
import {useEffect, useLayoutEffect, useMemo, useRef, useState} from 'react';
import {CancelOperationMessage} from '../../../api/Constants';
import {GroupsIcon} from '../../../icons';
import {currentFormatter, forceActiveFilter, useReportingPageStyles} from '../../reports';
import {groupNameComparer} from '../EquipmentExpenseResiduals/helpers';
import {getColumns} from './columns';
import {
  agentNameComparer,
  formatYearMonth,
  getAgentDropDownTitle,
  getGroupDropDownTitle,
} from './helpers';

const Filters: FilterOption[] = [
  {
    id: 'yearMonth',
    column: 'yearMonth',
    label: 'Date',
    type: 'text',
    force: true,
    operators: [
      {
        id: '__eq',
        label: 'equals',
      },
    ],
  },
];

const DefaultCriteria: SearchCriteria = {
  limit: 10, // page size
  offset: 1, // page
  order: 'desc',
  orderBy: 'yearMonth',
};

type AgentPayoutsToDisplay = {
  agentName: string;
  description: string;
  amount: number;
  isManual?: boolean;
  agentId: string;
  revShare?: string;
};

type Props = {
  agents: AgentView[] | SearchableAgentView[];
  groups: GroupView[];
};

export const ReferralOverridesResiduals = ({agents, groups}: Props): JSX.Element => {
  const classes = useReportingPageStyles();
  const api = useApiContainer();
  const [residuals, setResiduals] = useState<
    | (PaginatedResponse<ReferralOverrideResidual> & {
        bankcardSalesVolume: string;
        bankcardSalesVolumeAmex: string;
        transactionCount: string;
        transactionCountAmex: string;
        pinDebitSalesVolume: string;
        pinDebitCount: string;
        residualPaid: string;
        equipmentAgentExpense: string;
        overrideCount: string;
        overrideSplitPercent: string;
        overrideNetSplit: string;
      })
    | null
  >();
  const [loadingResiduals, setLoadingResiduals] = useState(true);
  const {getResidualAdjustments} = useCallable();
  const [_, setCancelTokenSource] = useState<CancelTokenSource | undefined>();
  const [searchCriteria, setSearchCriteria] = useState<SearchCriteria>(DefaultCriteria);
  const [filters, setFilters] = useState<{[_: string]: ActiveFilter}>({});
  const [filteredAgents, setFilteredAgents] = useState<AgentView[]>([]);
  const [selectedAgent, setSelectedAgent] = useState<AgentView | undefined>();
  const [selectedGroup, setSelectedGroup] = useState<GroupView | undefined>();
  const [yearMonths, setYearMonths] = useState<string[] | null>(null);
  const [selectedYearMonth, setSelectedYearMonth] = useState<string>();
  const [agentPayouts, setAgentPayouts] = useState<AgentPayoutsToDisplay[] | null>(null);
  const [totalAdj, setTotalAdj] = useState(0);
  const ref = useRef<HTMLDivElement>(null);
  const [width, setWidth] = useState<any>(0);

  const columns = useMemo(() => getColumns(), []);

  const getResiduals = () => {
    setLoadingResiduals(true);

    const cancelSource = api?.residuals.getCancelTokenSource();
    setCancelTokenSource(prev => {
      //bCheck if there are any previous pending requests
      if (prev !== undefined) {
        console.log('cancel');
        prev.cancel(CancelOperationMessage);
      }
      return cancelSource;
    });

    api?.residuals
      .getReferralOverridesResiduals(
        searchCriteria,
        Object.values(filters),
        selectedAgent?.id,
        selectedGroup?.id,
        cancelSource?.token
      )
      .then((result: any) => {
        setResiduals(result || null);
        setLoadingResiduals(false);
      })
      .catch((err: any) => {
        if (err?.message === CancelOperationMessage) {
          return;
        }
        console.error(err);
        setResiduals(null);
        setLoadingResiduals(false);
      });
  };

  useLayoutEffect(() => {
    if (loadingResiduals) return;
    setTimeout(() => {
      if (ref?.current) {
        setWidth(ref.current.offsetWidth);
      }
    }, 200);
  }, [loadingResiduals]);

  useEffect(() => {
    (async () => {
      const getPublishedResidualOptions = Firebase.functions.httpsCallable(
        CallableFunctionName.getPublishedResidualOptions
      );
      const result: {data: PublishedResidualOptionsResults} = await getPublishedResidualOptions({});
      if (!result?.data?.options) {
        setLoadingResiduals(false);
        return;
      }

      const options = result.data.options.sort();
      setSelectedYearMonth(options[options.length - 1]);
      setYearMonths(options);
      if (!options.length) {
        setLoadingResiduals(false);
      }

      const forcedFilter = forceActiveFilter(
        Filters,
        'yearMonth',
        '__eq',
        options[options.length - 1]
      );
      setFilters({...filters, yearMonth: forcedFilter});
    })();
  }, []);

  useEffect(() => {
    if (!selectedYearMonth) return;
    getResiduals();
  }, [searchCriteria, filters, selectedAgent, selectedGroup]);

  useEffect(() => {
    setFilteredAgents(agents);
  }, [agents]);

  useEffect(() => {
    if (!selectedYearMonth) {
      setAgentPayouts(null);
      return;
    }

    setAgentPayouts([]);

    getResidualAdjustments({
      yearMonth: selectedYearMonth,
      agentId: selectedAgent?.id,
      appendManualAdjustments: true,
    }).then(result => {
      if (result.status === 'ok') {
        const sortedData = result.data.sort((a, b) => a.agentId.localeCompare(b.agentId));
        setAgentPayouts(sortedData);
      }
    });
  }, [yearMonths, selectedAgent?.id, selectedYearMonth]);

  useEffect(() => {
    if (!agentPayouts || !residuals) {
      setTotalAdj(0);
      return;
    }

    const nextTotalAdj = agentPayouts.reduce(
      (previousValue, current) => previousValue + current.amount,
      0
    );

    setTotalAdj(nextTotalAdj);
  }, [residuals, agentPayouts]);

  const exportToCsv = async () => {
    if (!api) return;

    const options = {
      fieldSeparator: ',',
      filename: `residuals-referral-overrides-${selectedYearMonth}`,
      quoteStrings: '"',
      decimalSeparator: '.',
      showLabels: true,
      showTitle: false,
      title: 'Residuals Referral Overrides',
      useTextFile: false,
      useBom: true,
      useKeysAsHeaders: true,
    };

    const exportData = await api.residuals.getReferralOverridesResidualsExport(
      searchCriteria,
      Object.values(filters),
      selectedAgent?.id,
      selectedGroup?.id
    );

    if (!exportData || exportData.length === 0) return;

    const exporter = new ExportToCsv(options);

    const noNullData = JSON.parse(JSON.stringify(exportData).replace(/null/gi, '""'));

    exporter.generateCsv(noNullData);
  };

  const handleRetrieveData = (searchCriteria: SearchCriteria) => {
    setSearchCriteria(searchCriteria);
  };

  const handleDateChange = (event: any) => {
    const yearMonth = event.target.value;
    setSelectedYearMonth(yearMonth);
    const forcedFilter = forceActiveFilter(Filters, 'yearMonth', '__eq', yearMonth);
    setFilters({...filters, yearMonth: forcedFilter});
  };

  const handleDeleteFilter = (id: string) => () => {
    const _filters = {...filters};
    delete _filters[id];
    setFilters(_filters);
  };

  if (loadingResiduals) {
    return <Loading />;
  }

  if (!yearMonths || yearMonths.length === 0) {
    return (
      <Box mt={6}>
        <Typography variant="body1" align="center">
          No residuals have been added.
        </Typography>
      </Box>
    );
  }

  return (
    <div className={classes.root} ref={ref}>
      <Title breadcrumbs={[<Typography variant="body1">Referral Overrides Residuals</Typography>]}>
        <div className={classes.grow} />

        {groups.length > 0 && (
          <>
            <AutoCompleteInputBase
              selected={selectedGroup}
              setSelected={(group?: GroupView) => {
                setSelectedGroup(group);
                setSelectedAgent(undefined);
                setFilteredAgents(
                  group ? agents.filter(agent => agent.group?.id === group?.id) : agents
                );
              }}
              icon={GroupsIcon}
              placeholder="Select group..."
              options={groups}
              comparer={groupNameComparer}
              getOptionLabel={(group: GroupView) => getGroupDropDownTitle(group)}
              onItemSelect={(group: GroupView | null) => setSelectedGroup(group ?? undefined)}
            />
            <Divider orientation="vertical" className={classes.divider} flexItem />
          </>
        )}

        {filteredAgents.length > 0 && (
          <>
            <AutoCompleteInputBase
              selected={selectedAgent}
              setSelected={(agent?: AgentView) => setSelectedAgent(agent)}
              icon
              placeholder={filteredAgents?.length > 0 ? 'Select agent...' : 'No agents found'}
              options={filteredAgents}
              comparer={agentNameComparer}
              getOptionLabel={(agent: AgentView) => getAgentDropDownTitle(agent)}
              onItemSelect={(agent: AgentView | null) => setSelectedAgent(agent ?? undefined)}
            />

            <Divider orientation="vertical" className={classes.divider} flexItem />
          </>
        )}

        {yearMonths && (
          <TextField
            value={selectedYearMonth}
            onChange={handleDateChange}
            variant="standard"
            InputProps={{
              classes: {
                input: classes.selectInput,
              },
              disableUnderline: true,
            }}
            select
          >
            {yearMonths.map(e => {
              return (
                <MenuItem key={e} value={e}>
                  {formatYearMonth(e)}
                </MenuItem>
              );
            })}
          </TextField>
        )}
        <Divider orientation="vertical" className={classes.divider} flexItem />
        <Button onClick={exportToCsv}>Export</Button>
      </Title>

      {residuals && isEmpty(residuals.data) && (
        <Typography className={classes.noContent}>No Referral Overrides</Typography>
      )}

      {residuals && !isEmpty(residuals.data) && (
        <>
          <Grid container spacing={2} direction="row" alignItems="stretch">
            <Grid item xs={12}>
              {filters && !isEmpty(filters) && (
                <IconButton disabled size="large">
                  <FilterListIcon />
                </IconButton>
              )}

              {filters &&
                Object.keys(filters).map(key => {
                  const filter = filters[key];

                  if (filter.option.id === 'yearMonth') {
                    return (
                      <Chip
                        key={`${key}-${filter.operator.id}`}
                        className={classes.chip}
                        label={
                          <span>
                            <b>{filter.option.label}</b> {filter.operator.label} '
                            <b>{formatYearMonth(filter.value as string)}</b>'
                          </span>
                        }
                        variant="outlined"
                        onDelete={filter.option.force ? undefined : handleDeleteFilter(key)}
                      />
                    );
                  }

                  return (
                    <Chip
                      key={`${key}-${filter.operator.id}`}
                      className={classes.chip}
                      label={
                        <span>
                          <b>{filter.option.label}</b> {filter.operator.label} '
                          <b>
                            {filter.option.type === 'currency'
                              ? `$${filter.value}`
                              : `${filter.value}`}
                          </b>
                          '
                        </span>
                      }
                      variant="outlined"
                      onDelete={filter.option.force ? undefined : handleDeleteFilter(key)}
                    />
                  );
                })}
            </Grid>

            <Grid item xs={3}>
              <Square
                center
                lines={{
                  'Visa/MC/Disc Bankcard Transactions': parseFloat(
                    residuals?.transactionCount || '0'
                  ),
                }}
              />
            </Grid>

            <Grid item xs={3}>
              <Square
                center
                lines={{
                  'Amex Bankcard Transactions': parseFloat(residuals?.transactionCountAmex || '0'),
                }}
              />
            </Grid>

            <Grid item xs={3}>
              <Square
                center
                lines={{
                  'Visa/MC/Disc Bankcard Sales Volume': currentFormatter.format(
                    parseFloat(residuals?.bankcardSalesVolume || '0')
                  ),
                }}
              />
            </Grid>

            <Grid item xs={3}>
              <Square
                center
                lines={{
                  'Amex Bankcard Sales Volume': currentFormatter.format(
                    parseFloat(residuals?.bankcardSalesVolumeAmex || '0')
                  ),
                }}
              />
            </Grid>

            <Grid item xs={3}>
              <Square
                center
                lines={{
                  'EBT/PIN Debit Total Volume': currentFormatter.format(
                    parseFloat(residuals.pinDebitSalesVolume || '0')
                  ),
                }}
              />
            </Grid>

            <Grid item xs={3}>
              <Square
                center
                lines={{
                  'EBT/PIN Debit Transaction Count': residuals.pinDebitCount || '0',
                }}
              />
            </Grid>

            <Grid item xs={3}>
              <Square
                center
                lines={{
                  'Residual Paid': currentFormatter.format(
                    Number.parseFloat(residuals.overrideNetSplit || '0')
                  ),
                }}
              />
            </Grid>
          </Grid>

          <Box
            component={Paper}
            overflow="auto"
            mt={2}
            sx={[{'& > div:first-child': {width: '100%'}}, {width: {width}}]}
          >
            <DataTable
              noWrap={true}
              columns={columns}
              data={residuals}
              summary={{
                bankcardSalesVolume: currentFormatter.format(
                  parseFloat(residuals?.bankcardSalesVolume || '0')
                ),
                bankcardSalesVolumeAmex: currentFormatter.format(
                  parseFloat(residuals?.bankcardSalesVolumeAmex || '0')
                ),
                transactionCount: parseFloat(residuals?.transactionCount || '0'),
                transactionCountAmex: parseFloat(residuals?.transactionCountAmex || '0'),
                equipmentAgentExpense: currentFormatter.format(
                  parseFloat(residuals?.equipmentAgentExpense || '0')
                ),
                overrideCount: residuals?.overrideCount || '0',
                overrideSplitPercent: `${residuals?.overrideSplitPercent ?? 0}%`,
                overrideNetSplit: currentFormatter.format(
                  parseFloat(residuals?.overrideNetSplit || '0')
                ),
              }}
              onRowClick={() => {}}
              onRetrieveData={handleRetrieveData}
              paginate
              stickyHeader
              scrollableBody
              customHeight="75vh"
            />
          </Box>
        </>
      )}
    </div>
  );
};
